import React, { useState, useEffect } from 'react';
import { slangApi } from '../../services/api';
import { useApi } from '../../hooks/useApi';
import { TranslationResult } from '../../shared/types';
import './SlangTranslator.css';

const SlangTranslator: React.FC = () => {
  const [inputPhrase, setInputPhrase] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  
  const {
    data: translation,
    loading: translating,
    error: translationError,
    execute: translate,
    reset: resetTranslation
  } = useApi<TranslationResult>(slangApi.translate);

  const {
    loading: loadingSuggestions,
    execute: getSuggestions
  } = useApi<string[]>(slangApi.getSuggestions);

  // Get suggestions as user types
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (inputPhrase.trim().length > 2) {
        const result = await getSuggestions(inputPhrase.trim());
        if (result) {
          setSuggestions(result);
        }
      } else {
        setSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputPhrase, getSuggestions]);

  const handleTranslate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inputPhrase.trim()) {
      await translate(inputPhrase.trim());
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputPhrase(suggestion);
    setSuggestions([]);
  };

  const handleClear = () => {
    setInputPhrase('');
    setSuggestions([]);
    resetTranslation();
  };

  return (
    <div className="slang-translator fade-in">
      <div className="page-header slide-up">
        <h1 className="mumbai-accent">Mumbai Slang Translator</h1>
        <p>Decode the vibrant language of Mumbai streets and understand like a local Mumbaikar!</p>
      </div>
      
      <div className="translator-content">
        <form onSubmit={handleTranslate} className="translator-form slide-up">
          <div className="form-group">
            <label htmlFor="phrase-input" className="form-label">
              🗣️ Enter Mumbai slang or local phrase:
            </label>
            <div className="input-container">
              <input
                id="phrase-input"
                type="text"
                value={inputPhrase}
                onChange={(e) => setInputPhrase(e.target.value)}
                placeholder="e.g., 'bhidu', 'kya scene hai', 'jhakaas', 'solid'"
                className="form-input"
                disabled={translating}
              />
              {inputPhrase && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="clear-button"
                  aria-label="Clear input"
                >
                  ×
                </button>
              )}
            </div>
            
            {suggestions.length > 0 && (
              <div className="suggestions bounce-in">
                <p className="suggestions-label">💡 Suggestions:</p>
                <ul className="suggestions-list">
                  {suggestions.map((suggestion, index) => (
                    <li key={index}>
                      <button
                        type="button"
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="suggestion-item"
                      >
                        {suggestion}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="submit"
              disabled={!inputPhrase.trim() || translating}
              className="btn btn-primary"
            >
              {translating ? '🔄 Translating...' : '🚀 Translate'}
            </button>
          </div>
        </form>

        {translationError && (
          <div className="error slide-up">
            <strong>❌ Oops!</strong> {translationError}
          </div>
        )}

        {translation && (
          <div className="translation-result bounce-in">
            <h3>🎉 Translation Result</h3>
            <div className="result-card">
              <div className="original-phrase">
                <strong>📝 Original:</strong> {translation.originalPhrase}
              </div>
              <div className="translated-phrase">
                <strong>✨ Translation:</strong> {translation.translation}
              </div>
              <div className="confidence">
                <strong>🎯 Confidence:</strong> {Math.round(translation.confidence * 100)}%
              </div>
              {translation.alternatives && translation.alternatives.length > 0 && (
                <div className="alternatives">
                  <strong>🔄 Alternative meanings:</strong>
                  <ul>
                    {translation.alternatives.map((alt, index) => (
                      <li key={index}>{alt}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="help-section slide-up">
          <h3>🔥 Popular Mumbai Slang</h3>
          <div className="popular-terms">
            <button
              type="button"
              onClick={() => handleSuggestionClick('bhidu')}
              className="term-button"
            >
              Bhidu
            </button>
            <button
              type="button"
              onClick={() => handleSuggestionClick('kya scene hai')}
              className="term-button"
            >
              Kya scene hai
            </button>
            <button
              type="button"
              onClick={() => handleSuggestionClick('jhakaas')}
              className="term-button"
            >
              Jhakaas
            </button>
            <button
              type="button"
              onClick={() => handleSuggestionClick('solid')}
              className="term-button"
            >
              Solid
            </button>
            <button
              type="button"
              onClick={() => handleSuggestionClick('tapri')}
              className="term-button"
            >
              Tapri
            </button>
            <button
              type="button"
              onClick={() => handleSuggestionClick('bindaas')}
              className="term-button"
            >
              Bindaas
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlangTranslator;